import { useState } from "react";
import { Link, NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { SITE } from "@/config/site";
import DemoModeBanner from "@/components/feature/DemoModeBanner";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: "ri-dashboard-line", end: true },
  { to: "/dashboard/listings", label: "My Listings", icon: "ri-store-2-line", end: false },
  { to: "/dashboard/orders", label: "Orders & Invoices", icon: "ri-file-list-3-line", end: false },
  { to: "/dashboard/reviews", label: "Reviews", icon: "ri-star-line", end: false },
  { to: "/dashboard/leads", label: "Leads", icon: "ri-inbox-2-line", end: false },
  { to: "/dashboard/media", label: "Media Library", icon: "ri-image-2-line", end: false },
  { to: "/dashboard/profile", label: "My Profile", icon: "ri-user-settings-line", end: false },
];

export default function OwnerLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-50">
        <div className="flex flex-col items-center gap-3 text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
          <span className="text-sm">Loading your dashboard…</span>
        </div>
      </div>
    );
  }

  // Hard split: administrator / staff accounts belong in the console, never
  // in the owner dashboard.
  if (user && (user.role === "admin" || user.role === "staff")) {
    return <Navigate to="/admin" replace />;
  }

  if (!user) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background-50 px-4 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-100 text-primary-700">
          <i className="ri-lock-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-xl font-extrabold text-foreground-950">Please sign in</h1>
        <p className="mt-2 max-w-sm text-sm text-foreground-600">
          You need an owner account to view the dashboard.
        </p>
        <div className="mt-6 flex gap-3">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-login-box-line"></i> Sign in
          </Link>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-800 hover:border-primary-400 whitespace-nowrap cursor-pointer"
          >
            Create account
          </Link>
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const sidebar = (
    <div className="flex h-full flex-col">
      <Link to="/" className="flex items-center gap-2 px-5 py-5">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-foreground-950">
          <i className="ri-book-2-line text-lg"></i>
        </span>
        <span className="leading-tight">
          <span className="block font-heading text-sm font-extrabold text-foreground-950">Owner Portal</span>
          <span className="block text-[11px] text-foreground-500">Mysore Yellow Pages</span>
        </span>
      </Link>

      <nav className="flex-1 space-y-1 px-3 py-2">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => setMenuOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors cursor-pointer ${
                isActive ? "bg-primary-100 text-primary-800" : "text-foreground-700 hover:bg-background-100"
              }`
            }
          >
            <i className={`${item.icon} text-lg`}></i>
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-background-200 p-3">
        <div className="flex items-center gap-3 rounded-md bg-background-100/70 p-3">
          <img src={user.avatar} alt={user.name} className="h-9 w-9 rounded-full object-cover" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground-950">{user.name}</p>
            <p className="truncate text-[11px] text-foreground-500">{user.plan} plan</p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="mt-2 flex w-full items-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium text-secondary-700 hover:bg-secondary-50 cursor-pointer"
        >
          <i className="ri-logout-box-r-line text-lg"></i> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-background-100/60">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-background-200 bg-background-50">
        {sidebar}
      </aside>

      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-72 max-w-[80%] bg-background-50 border-r border-background-200">{sidebar}</div>
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
            className="flex-1 bg-foreground-950/40 backdrop-blur-sm cursor-pointer"
          ></button>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <DemoModeBanner />
        <header className="sticky top-0 z-40 flex h-16 items-center gap-3 border-b border-background-200 bg-background-50/95 px-4 md:px-6 backdrop-blur">
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
            className="lg:hidden flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700 cursor-pointer"
          >
            <i className="ri-menu-line text-xl"></i>
          </button>
          <div className="hidden sm:block text-sm text-foreground-500">
            Welcome back, <span className="font-semibold text-foreground-900">{user.name.split(" ")[0]}</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 hover:border-primary-400 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-external-link-line"></i>
              <span className="hidden sm:inline">View site</span>
            </Link>
            <Link
              to="/list-your-business"
              className="inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-3 py-2 text-sm font-semibold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-add-circle-line"></i>
              <span className="hidden sm:inline">Add listing</span>
            </Link>
          </div>
        </header>

        <main className="flex-1 px-4 py-6 md:px-6 md:py-8">
          <div className="mx-auto max-w-[1200px]">
            <Outlet />
          </div>
        </main>
        <footer className="border-t border-background-200 px-4 py-4 text-center text-xs text-foreground-500 md:px-6">
          © {new Date().getFullYear()} {SITE.name} — Owner Portal
        </footer>
      </div>
    </div>
  );
}